import { useState, useRef, useEffect } from 'react'
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion'
import { useNavigate } from 'react-router-dom'
import { ClipboardList, Calendar, Plus, FolderKanban, Zap, ListPlus, Sparkles } from 'lucide-react'
import { clsx } from 'clsx'
import { useUIStore, type CurrentView } from '@/stores/uiStore'
import { useLongPress } from '@/hooks/useLongPress'
import { springStiff } from '@/lib/motionConfig'
import { QuickAddSheet } from '@/components/tasks/QuickAddSheet'

export function BottomNav() {
  const navigate = useNavigate()
  const shouldReduceMotion = useReducedMotion()
  const currentView = useUIStore((state) => state.currentView)
  const setCurrentView = useUIStore((state) => state.setCurrentView)
  const openTaskCreateModal = useUIStore((state) => state.openTaskCreateModal)
  const [showQuickMenu, setShowQuickMenu] = useState(false)
  const [quickAddOpen, setQuickAddOpen] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)

  const { triggered, handlers: longPressHandlers } = useLongPress({
    delay: 400,
    onLongPress: () => {
      // 햅틱은 useLongPress 훅이 트리거 시점에 발화 (중복 호출 방지)
      setShowQuickMenu(true)
    },
  })

  const handleFabClick = () => {
    if (triggered.current) return
    openTaskCreateModal()
  }

  // Close menu on outside click + ESC key
  useEffect(() => {
    if (!showQuickMenu) return
    const handleClick = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setShowQuickMenu(false)
      }
    }
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setShowQuickMenu(false)
    }
    document.addEventListener('pointerdown', handleClick)
    document.addEventListener('keydown', handleKey)
    return () => {
      document.removeEventListener('pointerdown', handleClick)
      document.removeEventListener('keydown', handleKey)
    }
  }, [showQuickMenu])

  const handleNavigate = (target: CurrentView) => {
    if (target === 'tasks') {
      setCurrentView('tasks')
      navigate('/')
      return
    }
    if (target === 'calendar') {
      setCurrentView('calendar')
      navigate('/calendar')
      return
    }
    if (target === 'groups') {
      setCurrentView('groups')
      navigate('/groups')
      return
    }
    if (target === 'ai') {
      setCurrentView('ai')
      navigate('/ai')
      return
    }
  }

  // 채팅 탭은 그룹으로 통합됨 — 그룹 진입 시 채팅에 접근 (GroupPage 헤더 / 그룹 상세 채팅 탭).
  // 분석은 사이드/햄버거 메뉴로 이동 — 하단 탭은 핵심 4개 + 중앙 등록(+)만 유지.
  // 좌측 2개(작업·그룹) | 중앙 FAB | 우측 2개(AI·캘린더) = 4개 + FAB (대칭).
  const LEFT_COUNT = 2
  const navItems: { id: CurrentView; label: string; icon: typeof ClipboardList }[] = [
    // Left 2 (작업, 그룹)
    { id: 'tasks', label: '작업', icon: ClipboardList },
    { id: 'groups', label: '그룹', icon: FolderKanban },
    // --- FAB (+) 중앙 ---
    // Right 2 (AI, 캘린더)
    { id: 'ai', label: 'AI', icon: Sparkles },
    { id: 'calendar', label: '캘린더', icon: Calendar },
  ]

  const quickMenuItems = [
    {
      label: '빠른 추가',
      icon: Zap,
      action: () => {
        setShowQuickMenu(false)
        setQuickAddOpen(true)
      },
    },
    {
      label: '자세히 추가',
      icon: ListPlus,
      action: () => {
        setShowQuickMenu(false)
        openTaskCreateModal()
      },
    },
  ]

  return (
    <>
    <nav
      className="lg:hidden bottom-nav-foundation glass-heavy"
      aria-label="하단 메인 네비게이션"
    >
      <ul className="flex h-[60px] w-full items-center justify-evenly relative" role="menubar">
        {/* Left nav items (작업, 그룹) */}
        {navItems.slice(0, LEFT_COUNT).map((item) => {
          const Icon = item.icon
          const isActive = currentView === item.id
          return (
            <li key={item.label} className="flex-1" role="none">
              <button
                onClick={() => handleNavigate(item.id)}
                className={clsx(
                  'bottom-nav-item w-full focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary-500',
                  isActive ? 'bottom-nav-item--selected' : 'bottom-nav-item--default'
                )}
                role="menuitem"
                aria-current={isActive ? 'page' : undefined}
                aria-label={`${item.label}${isActive ? ' (현재 페이지)' : ''}`}
              >
                <Icon className="bottom-nav-icon fold:w-4 fold:h-4" aria-hidden="true" />
                <span className="bottom-nav-label-text fold:hidden">{item.label}</span>
                {isActive && (
                  <motion.span
                    layoutId="bottomNavIndicator"
                    className="absolute bottom-1 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full bg-primary-500 shadow-[0_0_8px_var(--color-primary-500)]"
                    aria-hidden="true"
                    transition={{ type: 'spring', stiffness: 400, damping: 28 }}
                  />
                )}
              </button>
            </li>
          )
        })}

        {/* Center FAB with long-press quick menu */}
        <li className="flex-1 flex justify-center relative" role="none">
          {/* Quick menu popup */}
          <AnimatePresence>
            {showQuickMenu && (
              <motion.div
                ref={menuRef}
                role="menu"
                aria-label="빠른 작업 메뉴"
                className="context-menu absolute bottom-20 left-1/2 min-w-[168px] origin-bottom"
                style={{ x: '-50%' }}
                initial={shouldReduceMotion ? { opacity: 0 } : { opacity: 0, scale: 0.9, y: 8 }}
                animate={
                  shouldReduceMotion
                    ? { opacity: 1 }
                    : { opacity: 1, scale: 1, y: 0, transition: springStiff }
                }
                exit={
                  shouldReduceMotion
                    ? { opacity: 0, transition: { duration: 0.12 } }
                    : { opacity: 0, scale: 0.9, y: 8, transition: { duration: 0.15 } }
                }
              >
                {quickMenuItems.map((item) => {
                  const Icon = item.icon
                  return (
                    <button
                      key={item.label}
                      type="button"
                      onClick={item.action}
                      role="menuitem"
                      className="context-menu__item w-full min-h-[44px]"
                    >
                      <Icon className="context-menu__icon text-primary-500" aria-hidden="true" />
                      {item.label}
                    </button>
                  )
                })}
              </motion.div>
            )}
          </AnimatePresence>
          <button
            onClick={handleFabClick}
            {...longPressHandlers}
            className="workspace-primary-action relative -mt-7 w-14 h-14 rounded-[20px] text-white el-fab flex items-center justify-center transition-all active:scale-95 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2"
            role="menuitem"
            aria-label="새 작업 추가 (길게 눌러 빠른 메뉴)"
            aria-haspopup="menu"
            aria-expanded={showQuickMenu}
          >
            <motion.span
              className="flex items-center justify-center"
              animate={shouldReduceMotion ? undefined : { rotate: showQuickMenu ? 45 : 0 }}
              transition={springStiff}
              aria-hidden="true"
            >
              <Plus className="w-7 h-7 fold:w-6 fold:h-6" />
            </motion.span>
          </button>
        </li>

        {/* Right nav items (AI, 캘린더, 분석) */}
        {navItems.slice(LEFT_COUNT).map((item) => {
          const Icon = item.icon
          const isActive = currentView === item.id
          return (
            <li key={item.label} className="flex-1" role="none">
              <button
                onClick={() => handleNavigate(item.id)}
                className={clsx(
                  'bottom-nav-item w-full focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary-500',
                  isActive ? 'bottom-nav-item--selected' : 'bottom-nav-item--default'
                )}
                role="menuitem"
                aria-current={isActive ? 'page' : undefined}
                aria-label={`${item.label}${isActive ? ' (현재 페이지)' : ''}`}
              >
                <Icon className="bottom-nav-icon fold:w-4 fold:h-4" aria-hidden="true" />
                <span className="bottom-nav-label-text fold:hidden">{item.label}</span>
                {isActive && (
                  <motion.span
                    layoutId="bottomNavIndicator"
                    className="absolute bottom-1 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full bg-primary-500 shadow-[0_0_8px_var(--color-primary-500)]"
                    aria-hidden="true"
                    transition={{ type: 'spring', stiffness: 400, damping: 28 }}
                  />
                )}
              </button>
            </li>
          )
        })}
      </ul>
    </nav>
    <QuickAddSheet open={quickAddOpen} onClose={() => setQuickAddOpen(false)} />
    </>
  )
}
